import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Cross-page user lookup cache.
 *
 * Many list views render `created_by`, `assignee_id`, etc. as raw IDs because
 * the backend rarely denormalizes the user name. <UserDisplay userId={...}/>
 * resolves the ID to a friendly label and surfaces a small "Utilisateur
 * supprimé" badge when the underlying user has been soft-deleted.
 *
 * Implementation:
 *  - Single global cache (Map) shared across all <UserDisplay> instances.
 *  - Pending IDs are batched within a 50ms tick → 1 round trip to
 *    `POST /api/users/lookup` per render burst.
 *  - Provider mounts once at the app root.
 */

const UserLookupContext = createContext(null);

export const UserLookupProvider = ({ children }) => {
  const { user, csrfToken } = useAuth();
  const cacheRef = useRef(new Map()); // user_id → resolved info or null
  const pendingRef = useRef(new Set());
  const subscribersRef = useRef(new Map()); // user_id → Set<callback>
  const flushTimerRef = useRef(null);

  const flush = useCallback(async () => {
    flushTimerRef.current = null;
    const ids = Array.from(pendingRef.current);
    if (!ids.length || !user) return;
    pendingRef.current = new Set();
    try {
      const r = await fetch(`${API}/api/users/lookup`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        body: JSON.stringify({ user_ids: ids }),
      });
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();
      for (const uid of ids) {
        cacheRef.current.set(uid, data[uid] || { user_id: uid, deleted: false, name: null });
        const subs = subscribersRef.current.get(uid);
        if (subs) {
          subs.forEach((cb) => cb(cacheRef.current.get(uid)));
        }
      }
    } catch {
      // On error, mark IDs as null-resolved to avoid infinite re-fetch
      for (const uid of ids) {
        if (!cacheRef.current.has(uid)) cacheRef.current.set(uid, null);
        const subs = subscribersRef.current.get(uid);
        if (subs) subs.forEach((cb) => cb(null));
      }
    }
  }, [user, csrfToken]);

  const request = useCallback(
    (userId, callback) => {
      // Already cached
      if (cacheRef.current.has(userId)) {
        callback(cacheRef.current.get(userId));
        return;
      }
      // Subscribe
      const subs = subscribersRef.current.get(userId) || new Set();
      subs.add(callback);
      subscribersRef.current.set(userId, subs);
      pendingRef.current.add(userId);
      // Schedule a flush
      if (!flushTimerRef.current) {
        flushTimerRef.current = setTimeout(flush, 50);
      }
    },
    [flush]
  );

  const unsubscribe = useCallback((userId, callback) => {
    const subs = subscribersRef.current.get(userId);
    if (subs) {
      subs.delete(callback);
      if (subs.size === 0) subscribersRef.current.delete(userId);
    }
  }, []);

  return (
    <UserLookupContext.Provider value={{ request, unsubscribe }}>
      {children}
    </UserLookupContext.Provider>
  );
};

const useUserLookup = () => {
  const ctx = useContext(UserLookupContext);
  return ctx;
};

/**
 * Render a user reference with optional "Utilisateur supprimé" badge.
 *
 * Props:
 *  - userId        : the user_id to resolve
 *  - fallback      : string to show while loading (default "Chargement…")
 *  - showEmail     : boolean — appends ` · email` when true
 *  - className     : passed through to the wrapping <span>
 *  - compact       : boolean — show "Supprimé" instead of "Utilisateur supprimé"
 */
const UserDisplay = ({
  userId,
  fallback = "—",
  showEmail = false,
  className = "",
  compact = false,
}) => {
  const ctx = useUserLookup();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!userId || !ctx) {
      setInfo(null);
      return;
    }
    let mounted = true;
    const cb = (data) => {
      if (mounted) setInfo(data);
    };
    ctx.request(userId, cb);
    return () => {
      mounted = false;
      ctx.unsubscribe(userId, cb);
    };
  }, [userId, ctx]);

  if (!userId) {
    return (
      <span className={className} data-testid="user-display-empty">
        {fallback}
      </span>
    );
  }
  if (info === null) {
    return (
      <span className={className} data-testid={`user-display-${userId}`}>
        <span className="text-zinc-500">{fallback}</span>
      </span>
    );
  }
  // info exists but might be {user_id, name: null} when not found / out of scope
  const name = info.name || info.email || userId;
  const isDeleted = info.deleted === true;
  return (
    <span
      className={className}
      data-testid={`user-display-${userId}`}
      data-deleted={isDeleted ? "true" : "false"}
    >
      <span className={isDeleted ? "text-zinc-500 line-through decoration-zinc-700" : ""}>
        {name}
      </span>
      {showEmail && info.email && info.email !== name && (
        <span className="text-zinc-500 ml-1">· {info.email}</span>
      )}
      {isDeleted && (
        <span
          className="ml-1.5 inline-flex items-center text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20 font-bold"
          title="Cet utilisateur a été supprimé"
        >
          {compact ? "Supprimé" : "Utilisateur supprimé"}
        </span>
      )}
    </span>
  );
};

export default UserDisplay;
export { UserDisplay };
